"use client";

/**
 * หน้าข้อมูล (รายรับ / รายจ่าย / ค้าง / ไฟล์ที่ได้รับ) ของ Bill-Lin
 * ใช้จากแท็บข้อมูล และเตรียมไว้ให้ chat hire ฝังได้ด้วย props ชุดเดียวกัน
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  AeraDataTable,
  WorkerTabSwitcher,
  WORKER_SCHEMAS,
  BillLinReceivableSchema,
  BillLinReceivableAddressSchema,
  BillLinReceivableSlipSchema,
  useWideDataLayout,
  type TableFilters,
  type DrawerAction,
  type Transaction,
  type TransactionSummary,
  type WorkspaceConfig,
  type WorkerSchema,
  CLIENT_ONLY_TABLE_FILTER_KEYS,
} from "@aera/data-table";
import { FileDown, ListFilter } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { FinancialWorkSectionHeader } from "@/app/financial-work/FinancialWorkSectionHeader";
import { DataSourcesStatusControl } from "@/app/financial-work/DataSourcesStatusControl";
import { FinancialWorkPreparingStatus, FinancialWorkLoadFailedPanel } from "@/app/financial-work/FinancialWorkViews";
import { useFinancialWorkWideRail } from "@/lib/financialWork/layout";
import { ReceivableOrdersPanel } from "@/app/artifacts/table/ReceivableOrdersSection";
import {
  RECEIVED_FILES_SCHEMA,
  ReceivedFilesPanel,
} from "@/app/artifacts/table/ReceivedFilesPanel";
import { IncomeMoneyActions } from "@/app/artifacts/table/IncomeMoneyActions";
import { LatestMoneyUndoBar } from "@/app/artifacts/table/LatestMoneyUndoBar";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import {
  BILLIN_RECEIVABLE_WORKER_ID,
  BILLIN_RECEIVABLE_SLIP_WORKER_ID,
  BILLIN_RECEIVABLE_ADDRESS_WORKER_ID,
  BILLIN_RECEIVED_FILES_WORKER_ID,
  isBillinReceivableWorkerId,
  receivableKindFromWorkerId,
} from "@/lib/billinConstants";
import type { ReceivableTabKind } from "@/lib/billinConstants";
import type { Workspace } from "@/lib/api";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { useAuthz } from "@/lib/authz";
import {
  parseTableUrl,
  sanitizeInternalReturnTo,
} from "./parseTableUrl";
import { applyNamedObjectQuery, consumeFinancialWorkOpenRecord } from "@/lib/financialWork/intent";
import {
  FINANCIAL_WORK_LOAD_FAIL_PARAM,
  FINANCIAL_WORK_NAV_PARAM,
} from "@/lib/financialWork/nav";
import type { ReceivedFileSource } from "@/lib/receivedFiles";
import { useFinancialWorkSearch } from "@/app/financial-work/FinancialWorkSearchContext";
import { AccountingPrepareDrawer } from "@/app/artifacts/accounting/AccountingPrepareDrawer";
import {
  openTransactionsPrintView,
  reserveTransactionsPrintViewTarget,
} from "@/lib/openTransactionsPrintView";
import { getCanonicalOrigin } from "@/lib/getCanonicalOrigin";
import { resolveOwnerUserId } from "@/lib/pendingOrders";
import {
  looksLikeLineEmbeddedBrowser,
  shouldTryLiffExternal,
} from "@/lib/openLineExternalBrowser";
import { ARTIFACTS_SUMMARY_EXTERNAL_AUTH_HASH_KEY } from "@/lib/artifactsSummaryExternalAuthBridge";
import {
  EMPTY_TRANSACTION_SUMMARY,
  fetchWorkspaceTransactionSummary,
} from "@/lib/transactionSummary";
import { requestReceivablePendingRefresh } from "@/lib/receivablePendingRefresh";
import {
  INCOME_MONEY_CHANGED_EVENT,
  isDataOriginIncomeMoneyReason,
  notifyIncomeMoneyChanged,
  takePendingDataRefresh,
  type IncomeMoneyChangedDetail,
} from "@/lib/incomeMoneyRefresh";
const marketApiFetch = createMarketApiFetch();

const DEFAULT_WORKER = "bill-lin-income";

function getRecordIdText(record: Transaction) {
  const raw = record.record_id as unknown;
  if (raw && typeof raw === "object" && "value" in raw) {
    const value = (raw as { value?: unknown }).value;
    return value == null ? "" : String(value);
  }
  return raw == null ? "" : String(raw);
}

function getApiInternalId(record: Transaction): string {
  const raw = (record as unknown as { id?: unknown }).id;
  return raw == null ? "" : String(raw);
}

function replaceUrlWithoutNavigation(url: URL) {
  try {
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl === currentUrl) return;

    window.history.replaceState(null, "", nextUrl);
  } catch (error) {
    console.warn("[ArtifactsTable] Failed to update URL state", error);
  }
}

/** Keep the shop already in the address bar when this view's prop is stale. */
function applyWorkspaceIdToUrl(url: URL, workspaceId: string | undefined) {
  const live = url.searchParams.get("workspaceId")?.trim();
  const next = workspaceId?.trim();
  if (live && next && live !== next) return;
  if (next) url.searchParams.set("workspaceId", next);
}

const RECEIVABLE_SCHEMA_BY_WORKER: Record<string, WorkerSchema> = {
  [BILLIN_RECEIVABLE_ADDRESS_WORKER_ID]: BillLinReceivableAddressSchema,
  [BILLIN_RECEIVABLE_SLIP_WORKER_ID]: BillLinReceivableSlipSchema,
  [BILLIN_RECEIVABLE_WORKER_ID]: BillLinReceivableSchema,
};

function resolveSchema(workerId: string) {
  if (isBillinReceivableWorkerId(workerId)) {
    return {
      workerKey: workerId,
      schema: RECEIVABLE_SCHEMA_BY_WORKER[workerId],
      isReceivable: true as const,
      receivableKind: receivableKindFromWorkerId(workerId)!,
    };
  }

  const id = workerId in WORKER_SCHEMAS ? workerId : DEFAULT_WORKER;
  return {
    workerKey: id,
    schema: WORKER_SCHEMAS[id],
    isReceivable: false as const,
    receivableKind: null as ReceivableTabKind | null,
  };
}

export type BillinDataViewProps = {
  workspaceId: string;
  serviceId?: string | null;
  googleConnected?: boolean;
  mode?: "embedded" | "full";
  containerWidth?: number;
  workspace?: Workspace | null;
  initialWorkerId?: string | null;
};

export function BillinDataView({
  workspaceId,
  serviceId,
  googleConnected = true,
  mode = "embedded",
  containerWidth,
  workspace = null,
  initialWorkerId,
}: BillinDataViewProps) {
  const isWideLayout = useWideDataLayout();
  /** ≥1440: shell locks to viewport. ≥900: table card still fills remaining height. */
  const fillShellHeight = useFinancialWorkWideRail();
  const fillTableHeight = isWideLayout;
  const fwSearch = useFinancialWorkSearch();
  const ownerUserId = useMemo(
    () => resolveOwnerUserId(workspace),
    [workspace],
  );
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const search = searchParams?.toString() ?? "";

  const parsed = useMemo(() => parseTableUrl(search), [search]);

  const [queryClient] = useState(() => new QueryClient());
  const resolvedInitial = resolveSchema(
    initialWorkerId?.trim() || parsed.workerId,
  );
  const [activeWorker, setActiveWorker] = useState(resolvedInitial.workerKey);
  const [highlight, setHighlight] = useState<string | null>(parsed.highlight);
  const [focusField, setFocusField] = useState<string | null>(
    parsed.focusField,
  );
  const [drawerTab, setDrawerTab] = useState<
    "details" | "allocation" | "history" | null
  >(parsed.drawerTab);
  const [, setSummary] = useState<TransactionSummary | null>(null);
  const [, setSummaryLoading] = useState(false);
  const [, setSummaryError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<Partial<TableFilters>>(
    parsed.initialFilters,
  );
  const [authCookieReady, setAuthCookieReady] = useState(false);
  const [surfacePending, setSurfacePending] = useState(true);
  const [surfaceFailed, setSurfaceFailed] = useState(false);
  /** True only after pending→ready without failure — blocks empty flash on failed↔retry races. */
  const [surfaceSettled, setSurfaceSettled] = useState(false);
  const [surfaceEpoch, setSurfaceEpoch] = useState(0);
  const [externalHint, setExternalHint] = useState<string | null>(null);
  const [accountingOpen, setAccountingOpen] = useState(false);
  const surfaceKeyRef = useRef(`${workspaceId}:${activeWorker}`);
  const surfaceWasPendingRef = useRef(true);
  /** Sync with settled — ignore late pending=true after first paint (reset/remount races). */
  const surfaceSettledRef = useRef(false);
  const nextSurfaceKey = `${workspaceId}:${activeWorker}`;
  if (surfaceKeyRef.current !== nextSurfaceKey) {
    surfaceKeyRef.current = nextSurfaceKey;
    setSurfacePending(true);
    setSurfaceFailed(false);
    setSurfaceSettled(false);
    surfaceWasPendingRef.current = true;
    surfaceSettledRef.current = false;
  }
  const handleSurfacePendingChange = useCallback((pending: boolean) => {
    // After the list has painted once, keep it mounted. Soft reload / filter
    // remount / resetQueries used to flip pending and flash กำลังเตรียมข้อมูล.
    if (pending && surfaceSettledRef.current) return;
    setSurfacePending(pending);
  }, []);
  const handleSurfaceLoadFailure = useCallback((failed: boolean) => {
    setSurfaceFailed(failed);
    if (failed) {
      setSurfacePending(false);
      setSurfaceSettled(false);
      surfaceSettledRef.current = false;
      surfaceWasPendingRef.current = false;
    }
  }, []);
  const retrySurface = useCallback(() => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (url.searchParams.get(FINANCIAL_WORK_LOAD_FAIL_PARAM) === "1") {
        url.searchParams.delete(FINANCIAL_WORK_LOAD_FAIL_PARAM);
        const next = `${url.pathname}${url.search}${url.hash}`;
        router.replace(next, { scroll: false });
      }
    }
    setSurfaceFailed(false);
    setSurfacePending(true);
    setSurfaceSettled(false);
    surfaceSettledRef.current = false;
    surfaceWasPendingRef.current = true;
    setSurfaceEpoch((n) => n + 1);
  }, [router]);

  // Ready only after a real pending→idle transition while not failed.
  // Clearing failure mid-flight without pending must stay on preparing, not empty.
  useEffect(() => {
    if (surfaceFailed) {
      setSurfaceSettled(false);
      surfaceSettledRef.current = false;
      surfaceWasPendingRef.current = false;
      return;
    }
    if (!authCookieReady) return;
    if (surfacePending) {
      surfaceWasPendingRef.current = true;
      return;
    }
    if (surfaceWasPendingRef.current) {
      setSurfaceSettled(true);
      surfaceSettledRef.current = true;
      surfaceWasPendingRef.current = false;
    }
  }, [authCookieReady, surfaceFailed, surfacePending]);

  // `useSearchParams()` does not update after `history.replaceState`, so only
  // re-apply URL → state when the router search string changes (real navigation).
  useEffect(() => {
    const p = parseTableUrl(search);
    const nextWorker = initialWorkerId?.trim()
      ? resolveSchema(initialWorkerId.trim()).workerKey
      : resolveSchema(p.workerId).workerKey;
    setActiveWorker(nextWorker);
    // Keep a consume()/session open-record highlight if the router search is
    // still catching up — wiping to null here races and skips the drawer.
    if (p.highlight) setHighlight(p.highlight);
    if (p.focusField) setFocusField(p.focusField);
    if (p.drawerTab) setDrawerTab(p.drawerTab);
    setCurrentFilters(p.initialFilters);

    // Legacy date sorts → created_at (เวลาสร้าง).
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      const sort = url.searchParams.get("filter_sort");
      const isBillinLedger =
        nextWorker === "bill-lin-income" || nextWorker === "bill-lin-expense";
      if (isBillinLedger && (sort === "recorded_at" || sort === "record_date")) {
        url.searchParams.set("filter_sort", "created_at");
        if (!url.searchParams.get("filter_dir")) {
          url.searchParams.set("filter_dir", "desc");
        }
        replaceUrlWithoutNavigation(url);
      }
    }
  }, [initialWorkerId, search]);

  useEffect(() => {
    const pending = consumeFinancialWorkOpenRecord();
    const fromUrl = parseTableUrl(search).highlight;
    const id = (
      pending?.recordId ||
      pending?.highlight ||
      fromUrl ||
      ""
    ).trim();
    const workerId = (pending?.workerId || "").trim();
    if (workerId) {
      setActiveWorker(resolveSchema(workerId).workerKey);
    }
    if (!id) return;
    setHighlight(id);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (workerId) url.searchParams.set("workerId", workerId);
    url.searchParams.set("recordId", id);
    url.searchParams.set("highlight", id);
    if (!url.searchParams.get("drawerTab")) {
      url.searchParams.set("drawerTab", "details");
    }
    const focus = parseTableUrl(search).focusField;
    if (focus) {
      setFocusField(focus);
      url.searchParams.set("focusField", focus);
    }
    const tab = parseTableUrl(search).drawerTab;
    if (tab) {
      setDrawerTab(tab);
      url.searchParams.set("drawerTab", tab);
    }
    replaceUrlWithoutNavigation(url);
    // Mount-only: session open-record + first URL snapshot after งานเงิน → ข้อมูล.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, []);

  useEffect(() => {
    const token = window.localStorage.getItem("auth_token")?.trim();
    if (!token) {
      setAuthCookieReady(true);
      return;
    }

    void fetch("/api/auth/set-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .catch((error) => {
        console.warn("[ArtifactsTable] Failed to sync auth cookie", error);
      })
      .finally(() => setAuthCookieReady(true));
  }, []);

  const workspaceConfig: WorkspaceConfig = useMemo(
    () => ({
      bill_lin_config: "A",
      scale: "Solo",
      google_connected: googleConnected,
    }),
    [googleConnected],
  );

  const { workerKey, schema: baseSchema, isReceivable, receivableKind } = useMemo(
    () => resolveSchema(activeWorker),
    [activeWorker],
  );

  const { can, ready: authzReady } = useAuthz(workspaceId);
  const tabAllowed: Record<string, boolean> = {
    "bill-lin-income": can("web.ws.data.income", "read", "แท็บ “รายรับ” ในหน้าข้อมูล"),
    "bill-lin-expense": can("web.ws.data.expense", "read", "แท็บ “รายจ่าย” ในหน้าข้อมูล"),
    [BILLIN_RECEIVABLE_ADDRESS_WORKER_ID]: can(
      "web.ws.data.address",
      "read",
      "แท็บ “ที่อยู่ค้าง” ในหน้าข้อมูล",
    ),
    [BILLIN_RECEIVABLE_SLIP_WORKER_ID]: can("web.ws.data.slip", "read", "แท็บ “สลิปค้าง” ในหน้าข้อมูล"),
    [BILLIN_RECEIVABLE_WORKER_ID]: can("web.ws.data.receivable", "read", "แท็บ “ยอดค้าง” ในหน้าข้อมูล"),
  };
  const canWriteData = can(
    "web.ws.data",
    "write",
    "แก้ช่องในหน้าต่างรายการ, ปุ่มย้าย/แบ่งยอดรายรับ, ปุ่มจับคู่/ตัดยอด/สร้างยอดค้าง/บันทึกที่อยู่ และแถบย้อนกลับเงินล่าสุด ในหน้าข้อมูล",
  );
  const canExportData = can(
    "web.ws.data",
    "export",
    "ปุ่ม “ดาวน์โหลดสรุป” ในหน้าต่างรายการ หน้าข้อมูล",
  );
  const canDeleteData = can(
    "web.ws.data",
    "delete",
    "ปุ่ม “ยกเลิกรายการ” (หน้าต่างรายรับ/รายจ่าย) และ “ลบรายการ” (หน้าต่างยอดค้าง/สลิปค้าง) ในหน้าข้อมูล",
  );
  const canOpenPeriods = can("web.ws.periods", "read", "ปุ่ม “เตรียมส่งบัญชี” หัวหน้าข้อมูล");
  const tabAllowedKey = Object.entries(tabAllowed)
    .filter(([, ok]) => ok)
    .map(([id]) => id)
    .join(",");

  const schema = useMemo<WorkerSchema>(
    () => ({
      ...baseSchema,
      columns: canWriteData
        ? baseSchema.columns
        : baseSchema.columns.map((col) => (col.editable ? { ...col, editable: false } : col)),
      drawerActions: baseSchema.drawerActions?.filter(
        (action) =>
          (action.id !== "download-summary" || canExportData) &&
          (action.id !== "cancel-record" || canDeleteData),
      ),
    }),
    [baseSchema, canDeleteData, canExportData, canWriteData],
  );

  const allowedWorkerSchemas = useMemo(() => {
    const allowed = new Set(tabAllowedKey.split(","));
    return Object.fromEntries(
      Object.entries(WORKER_SCHEMAS).filter(([id]) => allowed.has(id)),
    );
  }, [tabAllowedKey]);

  const receivableAppendTabs = useMemo(() => {
    const allowed = new Set(tabAllowedKey.split(","));
    return [
      {
        id: BILLIN_RECEIVABLE_ADDRESS_WORKER_ID,
        label: "ที่อยู่ค้าง",
        live: true,
        iconKey: "map-pin",
      },
      {
        id: BILLIN_RECEIVABLE_SLIP_WORKER_ID,
        label: "สลิปค้าง",
        live: true,
        iconKey: "receipt",
      },
      {
        id: BILLIN_RECEIVABLE_WORKER_ID,
        label: "ยอดค้าง",
        live: true,
        iconKey: "hand-coins",
      },
    ].filter((tab) => allowed.has(tab.id));
  }, [tabAllowedKey]);

  const firstAllowedWorker = tabAllowedKey.split(",").find(Boolean) ?? null;
  const workerAllowed = tabAllowed[workerKey] ?? false;

  const summaryRequestIdRef = useRef(0);
  const fetchSummaryFromApi = useCallback(
    async (filters: Partial<TableFilters>, options?: { silent?: boolean }) => {
      const ws = workspaceId?.trim() ?? "";
      if (!ws) {
        setSummary(EMPTY_TRANSACTION_SUMMARY);
        setSummaryLoading(false);
        return;
      }

      const silent = options?.silent ?? false;
      const debugEnabled =
        typeof window !== "undefined" &&
        window.localStorage?.getItem?.("AERA_DT_DEBUG") === "1";

      // Silent auto-refresh must not cancel / orphan the visible first load.
      // It only updates numbers if nothing newer started, and never toggles loading.
      if (silent) {
        const baselineId = summaryRequestIdRef.current;
        try {
          if (debugEnabled) {
            console.info("[ArtifactsTable] fetchSummary silent", {
              worker: schema.workerId,
              workspaceId: ws,
              filters,
            });
          }
          const parsed = await fetchWorkspaceTransactionSummary(
            ws,
            schema.workerId,
            filters,
          );
          if (baselineId !== summaryRequestIdRef.current) return;
          setSummary(parsed);
          setSummaryError(null);
          setSummaryLoading(false);
        } catch (error) {
          if (baselineId !== summaryRequestIdRef.current) return;
          console.warn("[ArtifactsTable] Silent summary refresh failed", error);
        }
        return;
      }

      const requestId = ++summaryRequestIdRef.current;
      try {
        setSummaryLoading(true);
        setSummaryError(null);
        if (debugEnabled) {
          console.info("[ArtifactsTable] fetchSummary", {
            worker: schema.workerId,
            workspaceId: ws,
            filters,
            requestId,
          });
        }
        const parsed = await fetchWorkspaceTransactionSummary(
          ws,
          schema.workerId,
          filters,
        );
        if (requestId !== summaryRequestIdRef.current) return;
        setSummary(parsed);
      } catch (error) {
        if (requestId !== summaryRequestIdRef.current) return;
        const message =
          error instanceof Error ? error.message : "unknown error";
        console.warn("[ArtifactsTable] Failed to fetch summary", error);
        setSummaryError(message);
        setSummary((prev) => prev ?? EMPTY_TRANSACTION_SUMMARY);
      } finally {
        if (requestId === summaryRequestIdRef.current) {
          setSummaryLoading(false);
        }
      }
    },
    [schema.workerId, workspaceId],
  );

  useEffect(() => {
    if (isReceivable) return;
    void fetchSummaryFromApi(currentFilters);
  }, [currentFilters, fetchSummaryFromApi, isReceivable, workerKey, workspaceId]);

  useWorkspaceActivityAutoRefresh(workspaceId, () => {
    if (isReceivable) return;
    fetchSummaryFromApi(currentFilters, { silent: true });
  });

  /** Drop + refetch ledger caches (match/cancel/undo) without waiting for poll. */
  const refreshTransactions = useCallback(async () => {
    // Match/cancel usually happens on สลิปค้าง/ยอดค้าง — รายรับ query is inactive.
    // resetQueries clears stale pages so switching to รายรับ never shows pre-match rows
    // (invalidate + refetch active-only left inactive cache until hard refresh).
    notifyIncomeMoneyChanged({ reason: "ledger_refresh" });
    await queryClient.resetQueries({ queryKey: ["transactions"] });
    if (!isReceivable) {
      void fetchSummaryFromApi(currentFilters, { silent: true });
    }
  }, [queryClient, isReceivable, fetchSummaryFromApi, currentFilters]);

  /**
   * Soft refresh while รายรับ drawer is open.
   * resetQueries clears rows → AeraDataTable shows full skeleton and unmounts the
   * record drawer (modal vanishes) until refetch completes — then drawer remounts.
   */
  const refreshTransactionsSoft = useCallback(async () => {
    notifyIncomeMoneyChanged({ reason: "ledger_refresh_soft" });
    await queryClient.invalidateQueries({ queryKey: ["transactions"] });
    await queryClient.refetchQueries({
      queryKey: ["transactions"],
      type: "active",
    });
    if (!isReceivable) {
      void fetchSummaryFromApi(currentFilters, { silent: true });
    }
  }, [queryClient, isReceivable, fetchSummaryFromApi, currentFilters]);

  /**
   * งานเงิน → ข้อมูล: refresh ledger without re-emitting income-money-changed
   * (that would bounce back into the work feed and loop).
   * Soft invalidate — resetQueries clears rows and re-triggers กำลังเตรียมข้อมูล
   * after the list already painted (prepare → list → prepare → list).
   */
  const syncLedgerFromFinancialWork = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["transactions"] });
    await queryClient.refetchQueries({
      queryKey: ["transactions"],
      type: "active",
    });
    if (!isReceivable) {
      void fetchSummaryFromApi(currentFilters, { silent: true });
    }
    requestReceivablePendingRefresh({
      silent: true,
      force: true,
      retries: 2,
      retryDelayMs: 180,
      reason: "financial_work_reload",
    });
  }, [queryClient, isReceivable, fetchSummaryFromApi, currentFilters]);

  // dataView unmounts on งานเงิน — consume sticky flag on remount; also listen live.
  useEffect(() => {
    if (takePendingDataRefresh()) {
      void syncLedgerFromFinancialWork();
    }
    const onMoneyChanged = (event: Event) => {
      const detail =
        (event as CustomEvent<IncomeMoneyChangedDetail>).detail ?? {};
      const reason = (detail.reason ?? "").trim();
      if (isDataOriginIncomeMoneyReason(reason)) return;
      if (reason && reason !== "financial_work_reload" && reason !== "financial_work_money_changed") {
        return;
      }
      void syncLedgerFromFinancialWork();
    };
    window.addEventListener(INCOME_MONEY_CHANGED_EVENT, onMoneyChanged);
    return () => {
      window.removeEventListener(INCOME_MONEY_CHANGED_EVENT, onMoneyChanged);
    };
  }, [syncLedgerFromFinancialWork]);

  const handleMoneyUndone = useCallback(async () => {
    // Don't remount ยอดค้าง/สลิปค้าง (that flashes loading). Ask the live panel to
    // silent-refetch with retries, and refresh รายรับ in parallel.
    notifyIncomeMoneyChanged({ reason: "money_undone" });
    requestReceivablePendingRefresh({
      silent: true,
      force: true,
      retries: 2,
      retryDelayMs: 180,
      reason: "money_undone",
    });
    // On รายรับ: soft refresh keeps the open record drawer mounted.
    // On สลิปค้าง/ยอดค้าง: hard reset so inactive รายรับ cache cannot show pre-match rows.
    if (isReceivable) {
      await refreshTransactions();
    } else {
      await refreshTransactionsSoft();
    }
  }, [isReceivable, refreshTransactions, refreshTransactionsSoft]);

  const handleDownloadPdf = useCallback(
    async (
      opts:
        | { mode: "all"; targetWindow?: Window | null }
        | { mode: "single"; record: Transaction },
    ) => {
      const targetWindow =
        opts.mode === "all"
          ? (opts.targetWindow ??
            reserveTransactionsPrintViewTarget({
              title: "ดาวน์โหลดสรุป",
            }))
          : null;
      const ws = workspaceId?.trim() ?? "";
      if (opts.mode === "all" && !ws) {
        openTransactionsPrintView({
          actionLabel: "ดาวน์โหลดสรุป",
          schemaLabel: schema.workerLabel,
          rows: [],
          warning:
            "ไม่พบ workspaceId — กรุณาเปิดลิงก์ที่มี workspaceId แล้วลองใหม่",
          targetWindow,
        });
        return;
      }

      try {
        const base = new URLSearchParams(window.location.search);
        const urlObj = new URL(schema.apiEndpoint, "http://localhost");
        const apiParams = urlObj.searchParams;
        apiParams.set("worker", schema.workerId);
        apiParams.set("workspace_id", ws);
        base.forEach((v, k) => {
          if (!k.startsWith("filter_")) return;
          const key = k.replace(/^filter_/, "");
          if (CLIENT_ONLY_TABLE_FILTER_KEYS.has(key)) return;
          // Legacy date sorts → created_at (เวลาสร้าง).
          if (key === "sort" && (v === "recorded_at" || v === "record_date")) {
            apiParams.set("sort", "created_at");
          } else {
            apiParams.set(key, v);
          }
        });
        // Default date filter = created_at (เวลาสร้าง).
        if (
          (schema.workerId === "bill-lin-income" ||
            schema.workerId === "bill-lin-expense") &&
          !apiParams.get("date_field") &&
          (apiParams.get("from") || apiParams.get("to"))
        ) {
          apiParams.set("date_field", "created_at");
        }

        const fetchPage = async (cursor?: string | null) => {
          const params = new URLSearchParams(apiParams.toString());
          if (cursor) params.set("cursor", cursor);
          const url = `${urlObj.pathname}?${params.toString()}`;
          const res = await marketApiFetch(url);
          if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(
              `Fetch failed: ${res.status}${text ? ` — ${text.slice(0, 180)}` : ""}`,
            );
          }
          return (await res.json()) as {
            data?: Transaction[];
            nextCursor?: string | null;
          };
        };

        if (opts.mode === "single") {
          openTransactionsPrintView({
            actionLabel: "ดาวน์โหลดสรุป",
            schemaLabel: schema.workerLabel,
            rows: [opts.record],
          });
          return;
        }

        // mode === "all": fetch all pages (with a safety cap)
        const MAX = 2000;
        const rows: Transaction[] = [];
        let cursor: string | null | undefined = null;
        for (let i = 0; i < 50; i++) {
          const page = await fetchPage(cursor);
          if (Array.isArray(page?.data)) rows.push(...page.data);
          cursor = page?.nextCursor ?? null;
          if (!cursor) break;
          if (rows.length >= MAX) break;
        }

        openTransactionsPrintView({
          actionLabel: "ดาวน์โหลดสรุป",
          schemaLabel: schema.workerLabel,
          rows,
          targetWindow,
        });
      } catch (e: unknown) {
        const detail = e instanceof Error ? e.message : "unknown error";
        // If "all" fails, fall back to single only when we have a record.
        if (opts.mode === "single") {
          openTransactionsPrintView({
            actionLabel: "ดาวน์โหลดสรุป",
            schemaLabel: schema.workerLabel,
            rows: [opts.record],
          });
          return;
        }
        openTransactionsPrintView({
          actionLabel: "ดาวน์โหลดสรุป",
          schemaLabel: schema.workerLabel,
          rows: [],
          warning: `โหลดข้อมูลไม่สำเร็จ (${detail})`,
          targetWindow,
        });
      }
    },
    [schema, workspaceId],
  );

  const openSummaryInExternalBrowser = useCallback(
    (params?: { id?: string; recordId?: string }) => {
      const origin = getCanonicalOrigin().trim() || window.location.origin;
      const p = new URLSearchParams();
      p.set("workspaceId", workspaceId.trim());
      if (serviceId?.trim()) p.set("serviceId", serviceId.trim());
      p.set("workerId", workerKey);
      // Helps downstream + detection in embedded browsers.
      p.set("openExternalBrowser", "1");
      // When opening from LIFF, we want the PDF page to "go back" into LIFF (and re-open the same modal)
      // rather than navigating within the external browser.
      p.set("returnToLiff", "1");
      if (params?.id?.trim()) p.set("id", params.id.trim());
      if (params?.recordId?.trim()) p.set("recordId", params.recordId.trim());
      try {
        const q = new URLSearchParams(window.location.search);
        // Ensure key context params exist so we don't fall back to the generic home view.
        q.set("workspaceId", workspaceId.trim());
        if (serviceId?.trim()) {
          q.set("serviceId", serviceId.trim());
          // Embedded FinancialAdmin view relies on `source=order`.
          if (!q.get("source")) q.set("source", "order");
        }
        q.set("workerId", workerKey);

        // Ensure the drawer can re-open on return.
        const candidateHighlight = params?.recordId?.trim() || "";
        const highlight = q.get("highlight")?.trim() || "";
        if (!highlight && candidateHighlight)
          q.set("highlight", candidateHighlight);

        // Return to the page user came from (supports both `/` and `/artifacts/table` full-screen).
        const returnTo = `${window.location.pathname}?${q.toString()}`;
        p.set("returnTo", returnTo);
      } catch {
        // ignore
      }
      const current = new URLSearchParams(window.location.search);
      current.forEach((v, k) => {
        if (k.startsWith("filter_")) p.set(k, v);
      });
      const path = `/artifacts/table/summary-external?${p.toString()}`;
      let url = `${origin}${path}`;
      const token = window.localStorage.getItem("auth_token")?.trim();
      if (token) {
        url += `#${ARTIFACTS_SUMMARY_EXTERNAL_AUTH_HASH_KEY}=${encodeURIComponent(token)}`;
      }

      // IMPORTANT: this must happen synchronously inside the click gesture
      // or iOS/LIFF will block the navigation/popup.
      const liffObj = (window as unknown as { liff?: unknown }).liff as
        | {
            openExternalUrl?: (u: string) => unknown;
            openWindow?: (
              p: { url: string; external?: boolean } | string,
            ) => unknown;
          }
        | undefined;
      try {
        if (typeof liffObj?.openExternalUrl === "function") {
          void liffObj.openExternalUrl(url);
          return true;
        }
      } catch {
        // ignore and fall through
      }
      try {
        if (typeof liffObj?.openWindow === "function") {
          void liffObj.openWindow({ url, external: true });
          return true;
        }
      } catch {
        // ignore and fall through
      }

      // Fallback: open in a new tab or replace current tab.
      try {
        const w = window.open(url, "_blank");
        if (w) return true;
      } catch {
        // ignore
      }
      // IMPORTANT: do NOT navigate current LIFF tab. Caller expects current page/modal to remain.
      return false;
    },
    [serviceId, workspaceId, workerKey],
  );

  const initialFiltersSignature = JSON.stringify(parsed.initialFilters);
  const initialFilters = useMemo(
    () => JSON.parse(initialFiltersSignature) as Partial<TableFilters>,
    [initialFiltersSignature],
  );

  const handleFilterChange = useCallback(
    (filters: TableFilters) => {
      setSummary(null);
      setCurrentFilters(filters);
      if (typeof window === "undefined") return;
      const url = new URL(window.location.href);
      applyWorkspaceIdToUrl(url, workspaceId);
      url.searchParams.set("workerId", workerKey);

      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith("filter_")) url.searchParams.delete(key);
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (value) url.searchParams.set(`filter_${key}`, value);
      });

      replaceUrlWithoutNavigation(url);
    },
    [workspaceId, workerKey],
  );

  const handleRecordSelect = useCallback((record: Transaction | null) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (record) {
      const recordId = getRecordIdText(record);
      if (!recordId) return;
      url.searchParams.set("highlight", recordId);
      url.searchParams.set("recordId", recordId);
      setHighlight(recordId);
    } else {
      // Parent is told null only after the drawer actually closed (see AeraDataTable).
      url.searchParams.delete("highlight");
      url.searchParams.delete("recordId");
      url.searchParams.delete("drawerTab");
      url.searchParams.delete("modal");
      url.searchParams.delete("focusField");
      setHighlight(null);
      setFocusField(null);
      setDrawerTab(null);
      replaceUrlWithoutNavigation(url);
      router.replace(`${pathname}${url.search}${url.hash}`, { scroll: false });
      return;
    }
    replaceUrlWithoutNavigation(url);
  }, [pathname, router]);

  const handleWorkerSwitch = useCallback(
    (nextId: string) => {
      setActiveWorker(nextId);
      setSummary(null);
      // keep current filters; API summary will refetch via workerKey effect
      if (typeof window === "undefined") return;
      const url = new URL(window.location.href);
      applyWorkspaceIdToUrl(url, workspaceId);
      url.searchParams.set("workerId", nextId);
      url.searchParams.delete("highlight");
      url.searchParams.delete("recordId");
      url.searchParams.delete("drawerTab");
      url.searchParams.delete("modal");
      url.searchParams.delete("focusField");
      setHighlight(null);
      setFocusField(null);
      setDrawerTab(null);
      replaceUrlWithoutNavigation(url);
    },
    [workspaceId],
  );

  // URL / default worker may be a tab this role cannot open.
  useEffect(() => {
    if (!authzReady || workerAllowed || !firstAllowedWorker) return;
    handleWorkerSwitch(firstAllowedWorker);
  }, [authzReady, firstAllowedWorker, handleWorkerSwitch, workerAllowed]);

  const accountingReturnTo = useMemo(() => {
    if (typeof window === "undefined") return "";
    try {
      const q = new URLSearchParams(window.location.search);
      q.set("workspaceId", workspaceId.trim());
      if (serviceId?.trim()) {
        q.set("serviceId", serviceId.trim());
        if (mode === "embedded" && !q.get("source")) q.set("source", "order");
      }
      q.set("workerId", workerKey);
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value) q.set(`filter_${key}`, String(value));
        else q.delete(`filter_${key}`);
      });
      return `${window.location.pathname}?${q.toString()}`;
    } catch {
      return "";
    }
  }, [currentFilters, mode, serviceId, workerKey, workspaceId]);

  const initialAccountingPeriod = useMemo(() => {
    const from = String(currentFilters.from || "").trim();
    const match = from.match(/^(\d{4}-\d{2})/);
    return match?.[1];
  }, [currentFilters.from]);

  /** Deep-link context when opened from ส่งบัญชี (same month / handoff status). */
  const accountingDeepLink = useMemo(() => {
    const params = new URLSearchParams(search);
    if (params.get("fromAccounting") !== "1") return null;
    const period = params.get("accountingPeriod")?.trim() || "";
    const back = new URLSearchParams();
    if (workspaceId) back.set("workspaceId", workspaceId);
    if (serviceId) back.set("serviceId", serviceId);
    const origin = sanitizeInternalReturnTo(
      params.get("accountingReturnTo")?.trim() ?? "",
      "",
    );
    if (origin) back.set("returnTo", origin);
    return {
      period,
      title: period ? `บัญชีรอบ ${period}` : "บัญชีรอบ",
      backHref: `/artifacts/accounting?${back.toString()}`,
    };
  }, [search, serviceId, workspaceId]);

  const openFullScreenHref = useMemo(() => {
    if (!serviceId) return null;
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams();
    p.set("workspaceId", workspaceId);
    p.set("serviceId", serviceId);
    if (workerKey) p.set("workerId", workerKey);

    const current = new URLSearchParams(window.location.search);
    current.forEach((v, k) => {
      if (k.startsWith("filter_")) p.set(k, v);
    });
    if (highlight) p.set("highlight", highlight);
    if (accountingDeepLink) {
      p.set("fromAccounting", "1");
      if (accountingDeepLink.period) p.set("accountingPeriod", accountingDeepLink.period);
    }
    return `/artifacts/table?${p.toString()}`;
  }, [accountingDeepLink, highlight, serviceId, workerKey, workspaceId]);

  const pageTitle = accountingDeepLink?.title ?? "รายการธุรกรรม";

  const accountingCta = (
    <AeraButton
      type="button"
      onClick={() => setAccountingOpen(true)}
      variant={isWideLayout ? "secondary" : "primary"}
      fontWeight="medium"
      size={isWideLayout ? "sm" : "md"}
      icon={<FileDown size={16} className="shrink-0" />}
      className={
        isWideLayout ? "shrink-0 cursor-pointer" : "w-full cursor-pointer"
      }
    >
      เตรียมส่งบัญชี
    </AeraButton>
  );

  const sourceStatusChip = (
    <DataSourcesStatusControl
      workspaceId={workspaceId}
      workspaceName={workspace?.name}
      serviceId={serviceId}
      size={isWideLayout ? "sm" : "md"}
    />
  );

  const dataPageIntro = (
    <BillinDataViewHeader
      wide={isWideLayout}
      sourceStatus={sourceStatusChip}
      accountingAction={canOpenPeriods ? accountingCta : undefined}
    />
  );

  const forceLoadFail =
    searchParams?.get(FINANCIAL_WORK_LOAD_FAIL_PARAM) === "1";
  const tableAllowed = authzReady && workerAllowed;
  const noTabAllowed = authzReady && firstAllowedWorker === null;
  const showFailed = !noTabAllowed && (forceLoadFail || surfaceFailed);
  const showPreparing =
    !showFailed &&
    !noTabAllowed &&
    (!authCookieReady || surfacePending || !surfaceSettled);

  const dataPreparing = (
    <div
      className="flex min-h-0 flex-1 items-center justify-start px-4 py-20"
      data-testid="fw-data-preparing"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="กำลังเตรียมข้อมูล"
    >
      <FinancialWorkPreparingStatus
        title="กำลังเตรียมข้อมูล"
        description="Bill-Lin กำลังรวบรวมรายการล่าสุดให้คุณ"
      />
    </div>
  );

  const workerTabs = (
    <div className="flex min-w-0 shrink-0 items-stretch border-b border-aera-lightgray bg-aera-white px-4 pt-3 rounded-t-2xl">
      <div className="flex h-full min-w-0 flex-1">
        <WorkerTabSwitcher
          activeWorker={workerKey}
          onSwitch={handleWorkerSwitch}
          workspaceConfig={workspaceConfig}
          schemas={allowedWorkerSchemas}
          appendTabs={receivableAppendTabs}
        />
      </div>
    </div>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {/* Failed uses the same in-card slot as กำลังเตรียมข้อมูล — keep header + worker tabs. */}
      <div
        className={
          fillShellHeight
            ? "flex min-h-0 flex-1 flex-col bg-aera-offwhite text-aera-charcoal max-[899px]:h-auto min-[900px]:h-full min-[900px]:bg-aera-white"
            : "flex min-h-full flex-1 flex-col bg-aera-offwhite text-aera-charcoal min-[900px]:bg-aera-white"
        }
      >
        {accountingDeepLink ? (
          <div className="flex shrink-0 items-center gap-2 px-4 py-3">
            <p className="truncate text-sm font-semibold text-aera-charcoal" title={pageTitle}>
              {pageTitle}
            </p>
          </div>
        ) : (
          <div className="shrink-0 px-4 pb-3 pt-3 min-[900px]:px-6 min-[900px]:pt-5">
            {dataPageIntro}
          </div>
        )}

        {canWriteData ? (
          <LatestMoneyUndoBar
            workspaceId={workspaceId}
            onUndone={handleMoneyUndone}
          />
        ) : null}
        {externalHint ? (
          <div className="shrink-0 border-b border-aera-lightgray bg-aera-white px-4 py-2 text-xs font-semibold text-aera-red">
            {externalHint}
          </div>
        ) : null}

        <div
          className={
            fillTableHeight
              ? "relative z-20 mx-4 mb-4 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-aera-lightgray bg-aera-white min-[900px]:mx-6 min-[900px]:mb-6"
              : "relative z-20 mx-4 mb-3 flex min-h-0 min-w-0 flex-1 flex-col overflow-visible rounded-2xl border border-aera-lightgray bg-aera-white"
          }
        >
          {workerTabs}
          {noTabAllowed ? (
            <p
              className="px-4 py-16 text-sm text-aera-gray"
              data-testid="fw-data-no-tab-access"
            >
              บทบาทของคุณยังไม่มีสิทธิ์ดูข้อมูลหมวดใดในร้านนี้
            </p>
          ) : null}
          {showPreparing ? dataPreparing : null}
          {showFailed ? (
            <FinancialWorkLoadFailedPanel onRetry={retrySurface} />
          ) : null}
          <div
            className={
              showPreparing || showFailed
                ? "hidden"
                : fillTableHeight
                  ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                  : "flex min-w-0 flex-col"
            }
          >
            {!tableAllowed ? null : isReceivable && receivableKind ? (
              <ReceivableOrdersPanel
                key={`${workerKey}-${workspaceId}-${surfaceEpoch}`}
                workspaceId={workspaceId}
                workspace={workspace}
                containerWidth={containerWidth}
                receivableKind={receivableKind}
                schema={schema}
                onMoneyChanged={refreshTransactions}
                onLoadingChange={handleSurfacePendingChange}
                onLoadFailure={handleSurfaceLoadFailure}
                fillViewportHeight={fillTableHeight}
                readOnly={!canWriteData}
                canDelete={canDeleteData}
              />
            ) : authCookieReady ? (
              <AeraDataTable
                key={`${workerKey}-${workspaceId}-${surfaceEpoch}`}
                schema={schema}
                workspaceId={workspaceId}
                workspaceConfig={workspaceConfig}
                apiFetch={marketApiFetch}
                initialFilters={initialFilters}
                openDrawerOnLoad={highlight}
                openDrawerFocusField={focusField}
                openDrawerTab={drawerTab}
                locale="th"
                containerWidth={containerWidth}
                externalSearchQuery={fwSearch?.searchQuery}
                onExternalSearchQueryChange={fwSearch?.setSearchQuery}
                onFilterChange={handleFilterChange}
                onRecordSelect={handleRecordSelect}
                onPendingChange={handleSurfacePendingChange}
                onLoadFailure={handleSurfaceLoadFailure}
                fillViewportHeight={fillTableHeight}
                detailsAddon={
                  workerKey === "bill-lin-income"
                    ? (record, { onAllocationCountChange, allocationTabActive }) => (
                        <IncomeMoneyActions
                          record={record}
                          workspaceId={workspaceId}
                          onApplied={handleMoneyUndone}
                          onAllocationCountChange={onAllocationCountChange}
                          allocationTabActive={allocationTabActive}
                          readOnly={!canWriteData}
                        />
                      )
                    : undefined
                }
                onDrawerAction={(action: DrawerAction, record: Transaction) => {
                  if (action.id !== "download-summary") return;
                  if (
                    shouldTryLiffExternal() ||
                    looksLikeLineEmbeddedBrowser()
                  ) {
                    const id = getApiInternalId(record);
                    const recordId = getRecordIdText(record);
                    const opened = openSummaryInExternalBrowser({
                      id: id?.trim() ? id : undefined,
                      recordId: recordId?.trim() ? recordId : undefined,
                    });
                    if (!opened) {
                      setExternalHint(
                        "ไม่สามารถเปิดเบราว์เซอร์ภายนอกได้จาก LINE (โปรลองเปิดใน Safari/Chrome หรือกด “เปิดเต็มจอ” ก่อน)",
                      );
                      window.setTimeout(() => setExternalHint(null), 4500);
                    }
                    return;
                  }
                  void handleDownloadPdf({ mode: "single", record });
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
      <AccountingPrepareDrawer
        open={accountingOpen}
        onOpenChange={setAccountingOpen}
        workspaceId={workspaceId}
        serviceId={serviceId}
        ownerUserId={ownerUserId}
        returnTo={accountingReturnTo}
        initialPeriod={initialAccountingPeriod}
      />
    </QueryClientProvider>
  );
}

/** @deprecated ใช้ BillinDataView — คงชื่อเดิมไว้ให้ import เก่า */
export const ArtifactsTablePanel = BillinDataView;

export function BillinDataViewHeader({
  wide,
  sourceStatus,
  accountingAction,
}: {
  wide: boolean;
  sourceStatus?: ReactNode;
  accountingAction?: ReactNode;
}) {
  return (
    <FinancialWorkSectionHeader
      wide={wide}
      eyebrow="ข้อมูลที่ใช้ทำงาน"
      eyebrowIcon={<ListFilter className="h-3.5 w-3.5" aria-hidden />}
      title="ข้อมูล"
      description="เปิดดูข้อมูลต้นทางแบบตาราง และกลับไปจัดการเรื่องที่ยังค้างได้ทันที"
      status={sourceStatus}
      action={accountingAction}
    />
  );
}

function SkeletonBar({ className }: { className: string }) {
  return (
    <div
      className={`animate-pulse rounded bg-aera-lightgray/70 ${className}`}
      aria-hidden
    />
  );
}

/** โครงหน้าข้อมูลตอนโหลด — chat hire ใช้ชุดนี้ได้โดยไม่ต้องมี workspace */
export function BillinDataViewSkeleton({
  wide = true,
}: {
  wide?: boolean;
}) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col bg-aera-offwhite text-aera-charcoal min-[900px]:bg-aera-white"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="กำลังเตรียมข้อมูล"
    >
      <div className="shrink-0 px-4 pb-3 pt-3 min-[900px]:px-6 min-[900px]:pt-5">
        <BillinDataViewHeader
          wide={wide}
          sourceStatus={
            wide ? <SkeletonBar className="h-8 w-44 rounded-full" /> : undefined
          }
          accountingAction={
            wide ? (
              <SkeletonBar className="h-9 w-36 rounded-xl" />
            ) : (
              <SkeletonBar className="h-10 w-full rounded-xl" />
            )
          }
        />
      </div>

      <div
        className={
          wide
            ? "relative z-20 mx-4 mb-4 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-aera-lightgray bg-aera-white min-[900px]:mx-6 min-[900px]:mb-6"
            : "relative z-20 mx-4 mb-3 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-aera-lightgray bg-aera-white"
        }
      >
        <div
          className="flex min-h-0 flex-1 items-center justify-center px-4 py-20"
          data-testid="fw-data-preparing"
        >
          <FinancialWorkPreparingStatus
            title="กำลังเตรียมข้อมูล"
            description="Bill-Lin กำลังรวบรวมรายการล่าสุดให้คุณ"
          />
        </div>
      </div>
    </div>
  );
}
